import { Col, Stack, Container } from "react-bootstrap";
import { useMemo, useState } from "react";
import LoadingIndicator from "../LoadingIndicator";
import ErrorResponse from "../ErrorResponse";
import ExportData from "../ExportData";
import useSortableData from "../useSortableData";
import SongSearchInput from "../SearchInput";
import { useResolvedRadioNames } from "../HelperHooks";
import EmptyRow from "../EmptyRow";
import { getTrendLabel } from "../HelperFunctions";
import SongRow from "./SongRow";
import SongsHeader from "./SongsHeader";
import SongCatalogueModal from "../songCatalogue/SongCatalogueModal";

export default function MostPlayedTable({ data, loading, error, competitorId }) {
    const [catalogueSong, setCatalogueSong] = useState(null);
    const [filterData, setFilterData] = useState({ searchString: "" });

    const resolvedRadios = useResolvedRadioNames();

    const competitorName = useMemo(() => resolvedRadios?.find(x => x.radio_id === competitorId)?.radio_name, [competitorId, resolvedRadios]);

    const songsMemo = useMemo(() => {
        if (data) {
            let filteredData = data.songs;
            if (filterData.searchString) {
                const raw = filterData.searchString.trim().toLowerCase();

                const andParts = raw
                    .split("+")
                    .map(p => p.trim())
                    .filter(Boolean);

                filteredData = filteredData.filter(x => {
                    const artist = x.artist.toLowerCase();
                    const title = x.title.toLowerCase();
                    const year = x.release_year?.toString() ?? "";

                    return andParts.every(part => {
                        let orTerms = part
                            .split(",")
                            .map(p => p.trim())
                            .filter(Boolean);

                        return orTerms.some(term => {
                            if (term.startsWith('"') && term.endsWith('"')) {
                                const exact = term.slice(1, -1).trim();
                                const regex = new RegExp(`\\b${exact}\\b`, "i");
                                return regex.test(artist) || regex.test(title) || year === exact;
                            }

                            return artist.includes(term) || title.includes(term) || year.startsWith(term);
                        });
                    });
                });
            }

            return filteredData;
        }
    }, [data, filterData.searchString]);

    const { sortedItems, requestSort, sortConfig } = useSortableData(songsMemo, { key: "spins", direction: "descending" });

    const csvHeaders = ["Artist", "Title", "Release Year", "Spins", "Trend"];
    const csvData = useMemo(() => {
        if (!sortedItems || !resolvedRadios) return [];
        return sortedItems.map(x => [x.artist, x.title, x.release_year, x.spins, getTrendLabel(x.trend)]);
    }, [resolvedRadios, sortedItems]);
    const csvFilename = useMemo(() => `Most Played ${competitorId ? "On " + competitorName : "In Market"}`, [competitorId, competitorName]);

    return (
        <>
            {loading ? (
                <LoadingIndicator width={75} />
            ) : error ? (
                <ErrorResponse errorCode={error} />
            ) : (
                data && (
                    <>
                        <Stack direction="horizontal" gap={2} className=" mb-2 flex-wrap-reverse">
                            <Col xs xl={5}>
                                <SongSearchInput filterData={filterData} setFilterData={setFilterData} />
                            </Col>
                            <Col xs={"auto"} className="ms-auto">
                                <Stack direction="horizontal" gap={1} className="ms-xl-auto w-100 w-xl-auto order-first order-xl-last">
                                    <span className="me-1 d-none d-md-inline-block">Download:</span>
                                    <ExportData data={csvData} headers={csvHeaders} filename={csvFilename} />
                                </Stack>
                            </Col>
                        </Stack>
                        <SongsHeader sortConfig={sortConfig} requestSort={requestSort} showTrend showSpins showReleaseYear />
                        <Container fluid>
                            {sortedItems?.length ? (
                                sortedItems.map(x => (
                                    <SongRow key={x.item_id} item={x} setCatalogueSong={setCatalogueSong} showTrend showSpins showReleaseYear />
                                ))
                            ) : (
                                <EmptyRow text="No Songs" />
                            )}
                        </Container>
                    </>
                )
            )}
            <SongCatalogueModal show={!!catalogueSong} onHide={() => setCatalogueSong(null)} song={catalogueSong} />
        </>
    );
}
